// ============================================================================
// Componente: TreinamentoForms.js
// Objetivo: Central de formulários dentro do Módulo de Treinamento & Qualidade.
// Padrão visual: azul. Renderiza os formulários inline (embedded) — mesmo padrão
//                do RHForms (módulo RH).
// Escopo: qualidade — Avaliação de Treinamento (ex-Avaliação TSV) e a
// Auditoria SA Quiosque (hoje em Histórico).
// ============================================================================

import React, { useState } from 'react';
import {
    Star,
    IceCream,
    FileEdit,
    LineChart,
    History,
    ArrowLeft,
    CheckCircle2,
} from 'lucide-react';

// DEM-00014: o preenchimento da TSV passou a ser VERSIONADO — este mini-hub
// (lista de versões → questionário + gerenciar versões) substitui o antigo
// TreinamentoFormsAvaliacaoTSV_Questionario (removido).
import AvaliacaoTSVPreencher from './AvaliacaoTSVPreencher';
import AvaliacaoTSVRelatorio from './TreinamentoFormsAvaliacaoTSV_Relatorio';
import CkQuiosqueRelatorio from './TreinamentoFormsCkQuiosque_Relatorio';
import { perfilNoModulo, temAcessoTotal } from '../../../utils/perfisDerivados';

const COR = '#0284c7';
const COR_ESCURA = '#0369a1';
const COR_FUNDO = '#e0f2fe';

const TreinamentoForms = () => {
    const [activeSubTab, setActiveSubTab] = useState('visao-geral');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const role = perfilNoModulo(user?.perfil, 'treinamento') || '';

    const isAdmin = role === 'Admin';
    const isDiretoria = role === 'Diretoria';
    const isTreinamento = role === 'Treinamento';

    // Permissão fina (espelha as allowedRoles das antigas rotas /forms/...):
    // qualidade é de Treinamento/Diretoria (e Admin por bypass).
    // ⚠️ Perfil com ACESSO TOTAL ao módulo (ex.: Operacional) também entra: ele já
    //    alcança o menu, a rota e o card do hub por `temAcessoTotal`; sem isto aqui,
    //    o card abria VAZIO. O backend já libera (acesso total a treinamento, sem
    //    exceção para /api/avaliacao), então não há risco de 403. Usa-se o perfil
    //    CRU (não o traduzido `role`) porque a chave de acesso total é o nome real.
    const canQualidade = isAdmin || isDiretoria || isTreinamento
        || temAcessoTotal(user?.perfil, 'treinamento');

    // Definição dos formulários e suas views (id da sub-aba → componente embedded)
    //
    // ⚠️ A "Auditoria SA" morava aqui e SAIU em 2026-09-23 (DEM-00011, Fase 4,
    // T22): virou "Avaliação de Segurança do Alimento" no módulo Operacional
    // — mudou de módulo, não de tabela (`dtbAuditoriaSA` continua a mesma).
    const FORMS = [
        {
            // DEM-00011 (2026-09-23): renomeada de "Avaliação TSV" para
            // "Avaliação de Treinamento" — rótulo apenas, id/tabela/rota
            // continuam os mesmos (`avaliacao-tsv`, `dtbAvaliacaoTSV`,
            // `/api/avaliacao`).
            id: 'avaliacao-tsv',
            title: 'Avaliação de Treinamento',
            desc: 'Avaliação técnica e acompanhamento das normas de serviço em restaurante.',
            icon: <Star size={28} />,
            views: [
                { key: 'avaliacao-tsv-quest', tipo: 'preencher', show: canQualidade, Componente: AvaliacaoTSVPreencher },
                { key: 'avaliacao-tsv-relat', tipo: 'relatorio', show: canQualidade, Componente: AvaliacaoTSVRelatorio },
            ],
        },
        {
            // DEM-00011 (Fase 4, T23, 2026-09-23): PERDEU o preenchimento — o
            // que ela media foi absorvido pela área Quiosque da Avaliação 360
            // (Operacional). Os 34 registros seguem consultáveis aqui, em
            // Treinamento (não muda de módulo, ao contrário da SA/T22) —
            // "nada é apagado". Mesmo tratamento do BSV: view única, só leitura.
            id: 'ck-quiosque',
            title: 'Auditoria SA Quiosque',
            desc: 'Descontinuada — a checagem dos Dessert Centers (ilhas e quiosques) virou parte da área Quiosque da Avaliação 360. Os 34 registros seguem consultáveis aqui, em histórico.',
            icon: <IceCream size={28} />,
            views: [
                { key: 'ck-quiosque-hist', tipo: 'historico', show: canQualidade, Componente: CkQuiosqueRelatorio },
            ],
        },
    ];

    // Mapa plano de sub-aba → view, para renderização
    const viewPorChave = {};
    FORMS.forEach((f) => f.views.forEach((v) => { viewPorChave[v.key] = v; }));

    const botaoTipo = {
        preencher: { label: 'Preencher', icon: <FileEdit size={16} /> },
        relatorio: { label: 'Relatórios', icon: <LineChart size={16} /> },
        // Card descontinuado (T23) — mesmo componente de relatório, rótulo
        // deixa claro que não há mais preenchimento.
        historico: { label: 'Histórico', icon: <History size={16} /> },
    };

    const FormCard = ({ form }) => {
        const visiveis = form.views.filter((v) => v.show);
        if (visiveis.length === 0) return null;

        return (
            <div
                style={{
                    backgroundColor: '#fff', borderRadius: '16px', padding: '24px',
                    border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    display: 'flex', flexDirection: 'column', height: '100%', transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ backgroundColor: `${COR}15`, color: COR, padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {form.icon}
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{form.title}</h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', flex: 1, marginBottom: '24px' }}>
                    {form.desc}
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {visiveis.map((v) => {
                        const t = botaoTipo[v.tipo];
                        const primario = v.tipo === 'preencher';
                        const style = primario
                            ? { backgroundColor: COR, color: '#fff', border: 'none' }
                            : { backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' };
                        return (
                            <button
                                key={v.key}
                                onClick={() => setActiveSubTab(v.key)}
                                style={{
                                    flex: 1, minWidth: '100px', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '8px', padding: '10px', borderRadius: '8px',
                                    fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'opacity 0.2s',
                                    ...style,
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                                onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
                            >
                                {t.icon} {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const VisaoGeral = () => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '24px',
        }}>
            {FORMS.map((f) => <FormCard key={f.id} form={f} />)}
        </div>
    );

    // Sub-aba ativa só é válida se existir e o usuário tiver permissão
    const viewAtiva = activeSubTab !== 'visao-geral' ? viewPorChave[activeSubTab] : null;
    const subViewValida = viewAtiva && viewAtiva.show;

    if (subViewValida) {
        const Componente = viewAtiva.Componente;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <button
                    onClick={() => setActiveSubTab('visao-geral')}
                    style={{
                        alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 16px', borderRadius: '8px', border: `1px solid ${COR_FUNDO}`,
                        backgroundColor: COR_FUNDO, color: COR_ESCURA, fontSize: '14px', fontWeight: '700',
                        cursor: 'pointer',
                    }}
                >
                    <ArrowLeft size={16} /> Voltar aos formulários
                </button>
                <Componente embedded />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '12px',
                borderBottom: '1px solid #e2e8f0', paddingBottom: '16px',
            }}>
                <button style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px',
                    backgroundColor: COR_FUNDO, color: COR, border: `1px solid ${COR}`,
                    borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'default',
                }}>
                    <CheckCircle2 size={18} /> Visão Geral
                </button>
            </div>

            <VisaoGeral />
        </div>
    );
};

export default TreinamentoForms;
